/**
 * Crypto Assets screen — admin payout pipeline dashboard.
 *
 * Four tabs:
 *   - Overview   — Monero wallet balance + balance outstanding + 30d status histogram
 *   - Balances   — publisher balances, paginated
 *   - Withdrawals — status-filtered queue + per-row tx/exchange inspect
 *   - Exchange   — admin-initiated XMR conversion (estimate → confirm → commit)
 *
 * Legacy used "Creator" terminology in places (Balances tab title, table
 * "Creator" column). The underlying server fields are `publisherName` /
 * `publisherEmail` — relabelled to "Publisher" for consistency with the
 * rest of admin.nurev.io. The /creator-balances backend endpoint path
 * is kept as-is to avoid breaking the live server.
 *
 * The Exchange tab uses a two-step confirm pattern (clicking Exchange
 * once flips the button to a red "Confirm" state, second click commits)
 * because these moves are irreversible — once XMR leaves the wallet,
 * it's spent. A typo-protected confirm step matters more than UX gloss.
 */
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  AdminWithdrawalRow,
  PublisherBalanceRow,
  RecentByStatus,
  WithdrawalStatus
} from '@/api/adminWallet';
import {
  useAdminWithdrawals,
  useCreateExchange,
  useExchangeCurrencies,
  useExchangeEstimate,
  usePublisherBalances,
  useWalletOverview,
  useWithdrawalStatus
} from '@/hooks/useAdminWallet';
import {
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtNum, formatUsd } from '@/lib/format';

const fmtXmr = (v?: number | null): string =>
  v != null && Number.isFinite(v) ? Number(v).toFixed(6) : '—';

const STATUS_FILTERS: Array<WithdrawalStatus | 'all'> = [
  'all',
  'initiated',
  'transferring',
  'block confirming',
  'converting currency',
  'completed',
  'failed'
];

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useWalletOverview();
  const wallet = data?.wallet;
  const balances = data?.balances;
  const ex = data?.exchanges;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Monero Wallet"
          value={wallet?.connected ? `${fmtXmr(wallet.balanceXmr)} XMR` : 'Offline'}
          sub={wallet?.height ? `block ${fmtNum(wallet.height)}` : undefined}
          color={wallet?.connected ? 'text-emerald-300' : 'text-red-300'}
          loading={isLoading}
        />
        <StatCard
          label="Total Balance Outstanding"
          value={formatUsd(balances?.totalBalance)}
          sub={`${fmtXmr(balances?.totalBalanceXmr)} XMR equivalent`}
          loading={isLoading}
        />
        <StatCard
          label="Active Exchanges"
          value={fmtNum(ex?.activeWithdrawals)}
          color={(ex?.activeWithdrawals || 0) > 0 ? 'text-amber-300' : undefined}
          loading={isLoading}
        />
        <StatCard
          label="Exchange Providers"
          value={fmtNum(ex?.providers?.length)}
          sub={ex?.providers?.join(', ') || 'none configured'}
          loading={isLoading}
        />
      </div>

      {ex?.recentByStatus && ex.recentByStatus.length > 0 && (
        <Card>
          <SectionLabel>Last 30 Days by Status</SectionLabel>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {ex.recentByStatus.map(s => (
              <RecentStatusCard key={s._id} s={s} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function RecentStatusCard({ s }: { s: RecentByStatus }) {
  return (
    <div className="rounded-lg bg-white/[0.03] p-3">
      <Badge value={s._id} />
      <p className="mt-2 text-lg font-bold text-nurev-text">{fmtNum(s.count)}</p>
      <p className="text-[10px] text-nurev-muted">
        {formatUsd(s.totalFromAmount)}
      </p>
    </div>
  );
}

// ─── Publisher Balances tab ───────────────────────────────────────────────

function BalancesTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePublisherBalances({ page, limit: 25 });
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-nurev-border px-5 py-3">
        <SectionLabel>
          Publisher Balances ({fmtNum(data?.totalDocs || 0)} with balance)
        </SectionLabel>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
              <th className="px-4 py-3 font-medium">Publisher</th>
              <th className="px-4 py-3 text-right font-medium">Balance</th>
              <th className="px-4 py-3 text-right font-medium">XMR Equivalent</th>
              <th className="px-4 py-3 font-medium">Payout Currency</th>
              <th className="px-4 py-3 font-medium">Payout Address</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-nurev-border/50">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : docs.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-12 text-center text-nurev-muted"
                >
                  No publishers with balance.
                </td>
              </tr>
            ) : (
              docs.map(d => <BalanceRow key={d._id} d={d} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="border-t border-nurev-border px-4 py-3">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </Card>
  );
}

function BalanceRow({ d }: { d: PublisherBalanceRow }) {
  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="px-4 py-2">
        <p className="font-medium text-nurev-text">{d.publisherName || '—'}</p>
        {d.publisherEmail && (
          <p className="text-[10px] text-nurev-muted">{d.publisherEmail}</p>
        )}
      </td>
      <td className="px-4 py-2 text-right font-mono text-nurev-text">
        {formatUsd(d.balance)}
      </td>
      <td className="px-4 py-2 text-right font-mono text-xs text-nurev-muted">
        {fmtXmr(d.balanceXmr)}
      </td>
      <td className="px-4 py-2">
        <span className="inline-block rounded bg-amber-500/15 px-2 py-0.5 text-xs uppercase text-amber-300 ring-1 ring-amber-500/30">
          {d.payoutCurrency || 'xmr'}
        </span>
      </td>
      <td className="max-w-[200px] truncate px-4 py-2 font-mono text-xs text-nurev-muted">
        {d.payoutAddress || '—'}
      </td>
    </tr>
  );
}

// ─── Withdrawals tab ──────────────────────────────────────────────────────

function WithdrawalsTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus | 'all'>(
    'all'
  );
  const [inspectId, setInspectId] = useState<string | null>(null);

  const params = useMemo<{
    page: number;
    limit: number;
    status: WithdrawalStatus | '';
  }>(
    () => ({
      page,
      limit: 20,
      status: statusFilter === 'all' ? '' : statusFilter
    }),
    [page, statusFilter]
  );
  const { data, isLoading } = useAdminWithdrawals(params);
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map(s => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
              statusFilter === s
                ? 'bg-amber-500 text-nurev-bg'
                : 'bg-white/[0.06] text-nurev-muted hover:bg-white/10'
            }`}
          >
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Publisher</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Currency</th>
                <th className="px-4 py-3 font-medium">Provider</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : docs.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No withdrawals match.
                  </td>
                </tr>
              ) : (
                docs.map(w => (
                  <WithdrawalRow
                    key={w._id}
                    w={w}
                    inspecting={inspectId === w._id}
                    onInspect={() =>
                      setInspectId(inspectId === w._id ? null : w._id)
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>

      {inspectId && <InspectPanel id={inspectId} />}
    </div>
  );
}

function WithdrawalRow({
  w,
  inspecting,
  onInspect
}: {
  w: AdminWithdrawalRow;
  inspecting: boolean;
  onInspect: () => void;
}) {
  const isXmr = w.fromCurrency?.toUpperCase() === 'XMR';
  return (
    <tr
      className={`border-b border-nurev-border/50 ${
        inspecting ? 'bg-white/[0.03]' : 'hover:bg-white/[0.02]'
      }`}
    >
      <td className="px-4 py-2 text-xs text-nurev-muted">
        {fmtDate(w.createdAt)}
      </td>
      <td className="px-4 py-2 text-xs text-nurev-text">{w.publisherName || '—'}</td>
      <td className="px-4 py-2">
        <Badge value={w.status} />
        {w.createdByAdmin && (
          <span className="ml-1 inline-block rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
            admin
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-right font-mono text-nurev-text">
        {isXmr ? fmtXmr(w.fromAmount) : formatUsd(w.fromAmount)}
        <span className="ml-1 text-[10px] text-nurev-muted">{w.fromCurrency}</span>
      </td>
      <td className="px-4 py-2 text-xs text-nurev-muted">
        <span className="uppercase">{w.outCurrency}</span>
        {w.toAmount && w.toAmount > 0 && (
          <span className="ml-1 text-[10px]">({w.toAmount})</span>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-nurev-muted">
        {w.exchangeProvider || '—'}
      </td>
      <td className="px-4 py-2">
        <button
          onClick={onInspect}
          className="text-[10px] text-amber-300 hover:text-amber-200"
        >
          {inspecting ? 'close' : 'inspect'}
        </button>
      </td>
    </tr>
  );
}

function InspectPanel({ id }: { id: string }) {
  const { data, isLoading } = useWithdrawalStatus(id);
  return (
    <Card>
      <SectionLabel>Withdrawal Detail</SectionLabel>
      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : !data ? (
        <p className="text-sm text-nurev-muted">Not found.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <p className="mb-1 text-xs text-nurev-muted">TX Hash</p>
            <p className="break-all font-mono text-xs text-nurev-text">
              {data.withdrawal?.txHash || '—'}
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs text-nurev-muted">On-Chain Status</p>
            {data.txStatus?.error ? (
              <p className="text-xs text-red-300">{data.txStatus.error}</p>
            ) : data.txStatus ? (
              <p className="text-xs text-nurev-text">
                {data.txStatus.status}
                {(data.txStatus.confirms || 0) > 0 &&
                  ` (${data.txStatus.confirms} confirmations)`}
              </p>
            ) : (
              <p className="text-xs text-nurev-muted">—</p>
            )}
          </div>
          <div>
            <p className="mb-1 text-xs text-nurev-muted">Exchange Status</p>
            {data.exchangeStatus?.error ? (
              <p className="text-xs text-red-300">{data.exchangeStatus.error}</p>
            ) : data.exchangeStatus ? (
              <p className="text-xs text-nurev-text">
                {data.exchangeStatus.status}
                {data.exchangeStatus.amount_to &&
                  ` — ${data.exchangeStatus.amount_to} received`}
              </p>
            ) : (
              <p className="text-xs text-nurev-muted">—</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Exchange tab ─────────────────────────────────────────────────────────

function ExchangeTab() {
  const [currency, setCurrency] = useState('');
  const [address, setAddress] = useState('');
  const [amountXmr, setAmountXmr] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);

  const { data: overview } = useWalletOverview();
  const walletXmr = overview?.wallet?.balanceXmr;

  const { data: currencies = [] } = useExchangeCurrencies();
  const amountNum = parseFloat(amountXmr) || 0;
  const { data: estimate, isFetching: estimating } = useExchangeEstimate({
    from: 'xmr',
    to: currency,
    amount: amountNum
  });

  const mutation = useCreateExchange();

  // Reset confirming flag any time inputs change
  useEffect(() => {
    setConfirming(false);
  }, [currency, address, amountXmr]);

  const handleSubmit = async () => {
    setStatus(null);
    if (!currency || !address || amountNum <= 0) {
      setStatus({ kind: 'err', text: 'Fill in all fields.' });
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    try {
      const res = await mutation.mutateAsync({
        address,
        currency,
        amountXmr: amountNum
      });
      setStatus({
        kind: 'ok',
        text: `Exchange created via ${res.provider} — TX ${res.txHash?.slice(0, 16)}…`
      });
      setConfirming(false);
      setCurrency('');
      setAddress('');
      setAmountXmr('');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error ||
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.message ||
        'Exchange failed.';
      setStatus({ kind: 'err', text: msg });
      setConfirming(false);
    }
  };

  return (
    <div className="max-w-xl">
      <Card>
        <SectionLabel>Exchange XMR</SectionLabel>

        {walletXmr != null && (
          <p className="mb-4 text-xs text-nurev-muted">
            Available:{' '}
            <span className="font-mono text-emerald-300">
              {fmtXmr(walletXmr)} XMR
            </span>
          </p>
        )}

        <div className="space-y-4">
          <Field label="Amount (XMR)">
            <input
              type="number"
              step="0.000001"
              value={amountXmr}
              onChange={e => setAmountXmr(e.target.value)}
              placeholder="0.000000"
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 font-mono text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
            />
          </Field>

          <Field label="To Currency">
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
            >
              <option value="">Select currency…</option>
              {(Array.isArray(currencies) ? currencies : []).map(c => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Destination Address">
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Wallet address…"
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 font-mono text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
            />
          </Field>

          {currency && amountNum > 0 && (
            <div className="rounded-lg bg-white/[0.03] p-3">
              <p className="text-xs text-nurev-muted">Estimated Receive</p>
              <p className="text-lg font-bold text-nurev-text">
                {estimating
                  ? '…'
                  : estimate?.estimate
                    ? `${estimate.estimate} ${currency.toUpperCase()}`
                    : '—'}
              </p>
              {estimate?.provider && (
                <p className="text-[10px] text-nurev-muted">
                  via {estimate.provider}
                </p>
              )}
            </div>
          )}

          {status && (
            <p
              className={`text-xs ${
                status.kind === 'ok' ? 'text-emerald-300' : 'text-red-300'
              }`}
            >
              {status.text}
            </p>
          )}

          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className={`w-full rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-50 ${
              confirming
                ? 'bg-red-500/80 text-white hover:bg-red-500'
                : 'bg-amber-500 text-nurev-bg hover:bg-amber-400'
            }`}
          >
            {mutation.isPending
              ? 'Processing…'
              : confirming
                ? 'Confirm — send XMR'
                : 'Exchange'}
          </button>

          {confirming && (
            <div className="text-center">
              <button
                onClick={() => setConfirming(false)}
                className="text-xs text-nurev-muted hover:text-nurev-text"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'balances', label: 'Publisher Balances' },
  { id: 'withdrawals', label: 'Withdrawals' },
  { id: 'exchange', label: 'Exchange' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function CryptoAssetsScreen() {
  const [tab, setTab] = useState<Tab>('overview');

  // Wallet overview also drives the available-XMR display on the Exchange
  // tab; prefetch via a parallel mount so switching tabs is instant.
  useWalletOverview();

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Crypto Assets
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Wallet balance, publisher balances, withdrawals, admin-initiated
            exchanges. Multi-provider router (ChangeNOW / Exolix / StealthEX)
            with automatic failover.
          </p>
        </div>
        <a
          href="https://app.nurev.io/crypto-assets"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'balances' && <BalancesTab />}
      {tab === 'withdrawals' && <WithdrawalsTab />}
      {tab === 'exchange' && <ExchangeTab />}
    </div>
  );
}

